import React from 'react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { NamedLink } from '../../../components';

import css from './SearchResultsHeading.module.css';

// Category levels are stored as pub_<key>1, pub_<key>2, … in the URL.
const MAX_CATEGORY_LEVELS = 3;

/**
 * The chosen category path, top level first, e.g. [Home, Kitchen].
 *
 * @param {Object} urlQueryParams validated search params
 * @param {Array} categories config.categoryConfiguration.categories
 * @param {string} categoryKey config.categoryConfiguration.key
 * @returns {Array<{ id: string, name: string, level: number }>}
 */
export const getSelectedCategoryPath = (urlQueryParams = {}, categories = [], categoryKey) => {
  const path = [];
  let options = categories;
  for (let level = 1; level <= MAX_CATEGORY_LEVELS; level++) {
    const id = urlQueryParams[`pub_${categoryKey}${level}`];
    const found = id ? (options || []).find(c => c.id === id) : null;
    if (!found) {
      break;
    }
    path.push({ id: found.id, name: found.name, level });
    options = found.subcategories;
  }
  return path;
};

/**
 * The search page's title and breadcrumb: "Home › Shop › Kitchen" over a
 * "Kitchen" heading, or "Results for “bamboo”" for a keyword search — so the
 * shopper always knows where they are, rather than only seeing a result count.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.urlQueryParams validated search params
 * @param {Array} props.categories config.categoryConfiguration.categories
 * @param {string} props.categoryKey config.categoryConfiguration.key
 * @param {number} [props.resultsCount] how many products match
 * @param {boolean} [props.showCount] whether the count is known yet
 * @returns {JSX.Element}
 */
const SearchResultsHeading = props => {
  const { urlQueryParams = {}, categories, categoryKey, resultsCount, showCount } = props;
  const intl = useIntl();
  const categoryPath = getSelectedCategoryPath(urlQueryParams, categories, categoryKey);
  const currentCategory = categoryPath[categoryPath.length - 1];
  const keywords = urlQueryParams.keywords;

  const title = keywords
    ? intl.formatMessage({ id: 'SearchResultsHeading.keywordsTitle' }, { keywords })
    : currentCategory
    ? currentCategory.name
    : intl.formatMessage({ id: 'SearchResultsHeading.allProducts' });

  // Each crumb keeps the category levels above it and drops everything else.
  const crumbSearch = level =>
    `?${categoryPath
      .filter(c => c.level <= level)
      .map(c => `pub_${categoryKey}${c.level}=${encodeURIComponent(c.id)}`)
      .join('&')}`;

  return (
    <div className={css.root}>
      <nav aria-label={intl.formatMessage({ id: 'SearchResultsHeading.breadcrumbLabel' })}>
        <ol className={css.breadcrumbs}>
          <li className={css.crumb}>
            <NamedLink name="LandingPage" className={css.crumbLink}>
              <FormattedMessage id="SearchResultsHeading.home" />
            </NamedLink>
          </li>
          <li className={css.crumb}>
            {categoryPath.length > 0 || keywords ? (
              <NamedLink name="SearchPage" className={css.crumbLink}>
                <FormattedMessage id="SearchResultsHeading.shop" />
              </NamedLink>
            ) : (
              <span aria-current="page">
                <FormattedMessage id="SearchResultsHeading.shop" />
              </span>
            )}
          </li>
          {categoryPath.map((category, i) => {
            const isLast = i === categoryPath.length - 1 && !keywords;
            return (
              <li key={category.id} className={css.crumb}>
                {isLast ? (
                  <span aria-current="page">{category.name}</span>
                ) : (
                  <NamedLink
                    name="SearchPage"
                    to={{ search: crumbSearch(category.level) }}
                    className={css.crumbLink}
                  >
                    {category.name}
                  </NamedLink>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      <div className={css.titleRow}>
        <h1 className={css.title}>{title}</h1>
        {showCount ? (
          <span className={css.count}>
            <FormattedMessage
              id="SearchResultsHeading.count"
              values={{ count: resultsCount || 0 }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default SearchResultsHeading;
